import React, { memo, useMemo } from 'react';
import { HighchartsReact } from 'investira.react.charts';
import { dates, formats } from 'investira.sdk';
import { Card, Typography, Stack, useTheme } from 'investira.react.components';

const VariacaoChart = memo(props => {
  const { matriz, itens, dias } = props;

  const theme = useTheme();

  // Função para obter o nome do ativo/indicador baseado nos itens
  const getAtivoName = (pAtivoId, pItens) => {
    if (!pItens || !pItens[pAtivoId]) {
      // Fallback: limpar prefixos se não tiver itens
      return pAtivoId.replace(/^(ATIVO_|INDICADOR_)/, '');
    }

    const xItem = pItens[pAtivoId];

    // Se o nome for maior que 14 caracteres, reduz e adiciona "..."
    let xNome = '';
    if (xItem.ativo_seuid) {
      xNome = xItem.ativo_seuid;
    } else if (xItem.indicador_id) {
      xNome = xItem.indicador_id;
    } else if (xItem.ticker) {
      xNome = xItem.ticker;
    } else if (xItem.isin) {
      xNome = xItem.isin;
    } else {
      xNome = pAtivoId.replace(/^(ATIVO_|INDICADOR_)/, '');
    }

    if (xNome.length > 14) {
      xNome = xNome.slice(0, 14) + '...';
    }
    return xNome;
  };

  // Função para processar os dados da matriz
  const processData = useMemo(() => {
    if (!matriz || typeof matriz !== 'object') {
      return { series: [], dates: [] };
    }

    // Extrair todas as datas e ordená-las
    const xSortedDates = Object.keys(matriz).sort();

    // Extrair todos os ativos únicos
    const ativos = new Set();
    xSortedDates.forEach(date => {
      Object.keys(matriz[date]).forEach(ativo => {
        ativos.add(ativo);
      });
    });

    // Processar dados para cada ativo
    const series = Array.from(ativos)
      .map(ativo => {
        const data = [];

        xSortedDates.forEach(date => {
          if (matriz[date] && matriz[date][ativo]) {
            const variacaoAcumulada = matriz[date][ativo].variacao_acumulada;

            // Verificar se o valor é válido (não null, undefined, NaN)
            if (variacaoAcumulada == null || isNaN(variacaoAcumulada)) {
              return; // Pular este ponto se não for válido
            }

            // Normalizar para começar em 0% - subtrair o valor da primeira data
            const primeiraData = xSortedDates[0];
            const valorInicial =
              matriz[primeiraData] && matriz[primeiraData][ativo]
                ? matriz[primeiraData][ativo].variacao_acumulada
                : 0;

            // Verificar se o valor inicial é válido
            if (valorInicial == null || isNaN(valorInicial)) {
              return; // Pular este ponto se valor inicial não for válido
            }

            // Calcular a variação percentual em relação ao valor inicial
            let valorNormalizado;
            if (valorInicial === 0) {
              valorNormalizado = 0;
            } else {
              // valorNormalizado =
              //   ((variacaoAcumulada - valorInicial) / valorInicial) * 100;

              valorNormalizado = (variacaoAcumulada / valorInicial - 1) * 100;
            }

            // Verificar se o resultado final é válido
            if (isNaN(valorNormalizado) || !isFinite(valorNormalizado)) {
              return; // Pular este ponto se resultado não for válido
            }

            // Só adicionar o ponto se tiver dados válidos
            data.push([dates.toDate(date).getTime(), valorNormalizado]);
          }
          // Se não tiver dados, simplesmente não adiciona nada ao array
        });

        // Só retornar a série se tiver pelo menos um ponto válido
        if (data.length === 0) {
          return null;
        }

        return {
          name: getAtivoName(ativo, itens), // Usar função para obter nome correto
          type: 'line',
          data: data,
          connectNulls: true, // Não conectar pontos null
          marker: {
            enabled: false
          }
        };
      })
      .filter(serie => serie !== null); // Filtrar séries nulas

    return { series, dates: xSortedDates };
  }, [matriz, itens]);

  const xOptions = {
    title: {
      text: ``,
      style: {
        fontSize: '16px',
        fontWeight: 'bold',
        color: theme.palette.text.primary
      }
    },
    chart: {
      type: 'line',
      height: 400,
      backgroundColor: 'transparent',
      zooming: {
        type: 'x'
      },
      events: {
        load: function () {
          // Auto-ajustar o zoom para mostrar todos os dados
          if (processData.series.length > 0) {
            this.xAxis[0].setExtremes();
          }
        }
      }
    },
    xAxis: {
      type: 'datetime',
      title: {
        text: 'Data',
        style: {
          color: theme.palette.text.primary
        }
      },
      crosshair: true,
      labels: {
        style: {
          color: theme.palette.text.primary
        }
      },
      gridLineColor: '#e0e0e0'
    },
    yAxis: {
      title: {
        text: 'Variação Acumulada (%)',
        style: {
          color: theme.palette.text.primary
        }
      },
      labels: {
        formatter: function () {
          return this.value.toFixed(2) + '%';
        },
        style: {
          color: theme.palette.text.primary
        }
      },
      plotLines: [
        {
          value: 0,
          color: '#7A81A4',
          width: 1,
          zIndex: 1,
          dashStyle: 'dash'
        }
      ],
      gridLineColor: '#e0e0e0'
    },
    tooltip: {
      shared: true,
      crosshairs: true,
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#ccc',
      borderRadius: 8,
      shadow: true,
      formatter: function () {
        const date = formats.formatDateCustom(this.x, 'DD/MM/YYYY');
        let tooltip = `<div style="font-weight: bold; margin-bottom: 5px;">${date}</div>`;

        this.points.forEach(point => {
          // Verificar se o valor é válido antes de exibir
          if (point.y != null && !isNaN(point.y) && isFinite(point.y)) {
            const valor = point.y.toFixed(2);
            const cor = point.y >= 0 ? '#00dfa8' : '#ed442c';
            tooltip += `
              <div style="display: flex; align-items: center; margin: 2px 0;">
                <span style="color:${point.color}; font-weight: bold; margin-right: 6px;">●</span>
                <span style="color: #2e2e46; margin-right: 6px;">${point.series.name}:</span>
                <span style="color: ${cor}; font-weight: bold;">${valor}%</span>
              </div>
            `;
          }
        });

        return tooltip;
      }
    },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: {
        color: theme.palette.text.primary
      }
    },
    plotOptions: {
      line: {
        lineWidth: 2,
        connectNulls: true, // Não conectar pontos null globalmente
        marker: {
          enabled: false,
          states: {
            hover: {
              enabled: true,
              radius: 4
            }
          }
        },
        states: {
          hover: {
            lineWidth: 3
          }
        }
      }
    },
    credits: {
      enabled: false
    },
    series: processData.series
  };

  if (!processData.series.length) {
    return null;
  }

  return (
    <Card>
      <Stack px={2} py={2}>
        <Stack mb={2}>
          <Typography variant={'h6'}>Variação Acumulada </Typography>
          <Typography variant={'body2'}>Normalizada nos últimos {dias} dias.</Typography>
        </Stack>

        <HighchartsReact options={xOptions} drilldown />
      </Stack>
    </Card>
  );
});

export default VariacaoChart;
